import { useEffect, useState } from "react";
import type { AuditoriaEventoItem } from "../../../shared/types";
import { Cargando } from "../../components/Cargando";
import { EstadoVacio } from "../../components/EstadoVacio";
import { Tarjeta } from "../../components/Tarjeta";
import { api, ErrorApi } from "../../lib/apiClient";

const ETIQUETAS_ENTIDAD: Record<string, string> = {
  evento_agenda: "Vencimiento",
  actuacion: "Actuación",
  expediente: "Expediente",
  documento: "Documento",
  parte: "Parte",
  honorario: "Honorario",
  rendicion: "Rendición",
  movimiento_economico: "Movimiento económico",
};

// Traduce accion + detalle (JSON de una lista blanca de campos escalares,
// ver lib/auditoria-temporal.ts) a una frase legible — nunca se muestra el
// JSON crudo: para cualquier acción no contemplada acá, el fallback sigue
// siendo texto humanizado a partir del propio nombre de la acción.
function descripcionEvento(accion: string, detalle: Record<string, unknown>): string {
  switch (accion) {
    case "eventos_agenda.crear":
      return `Creó el vencimiento${detalle.tipoEvento ? ` "${detalle.tipoEvento}"` : ""}`;
    case "eventos_agenda.editar":
      return "Editó el vencimiento";
    case "eventos_agenda.cambiar_estado":
      return `Cambió el estado de "${detalle.estadoAnterior ?? "?"}" a "${detalle.estadoNuevo ?? "?"}"`;
    case "actuaciones.crear":
      return `Creó la actuación${detalle.tipo ? ` (${detalle.tipo})` : ""}${detalle.conVencimiento ? " con vencimiento" : ""}`;
    case "actuaciones.editar":
      return "Editó la actuación";
    case "actuaciones.eliminar":
      return "Eliminó la actuación";
    case "expedientes.crear":
      return "Creó el expediente";
    case "expedientes.editar":
      return "Editó datos del expediente";
    case "expedientes.cambiar_estado":
      return `Cambió el estado de "${detalle.estadoAnterior ?? "?"}" a "${detalle.estadoNuevo ?? "?"}"`;
    case "documentos.subir":
      return `Subió el documento${detalle.titulo ? ` "${detalle.titulo}"` : ""}`;
    case "documentos.eliminar":
      return `Eliminó el documento${detalle.titulo ? ` "${detalle.titulo}"` : ""}`;
    case "partes.crear":
      return `Agregó una parte${detalle.nombreCompleto ? ` (${detalle.nombreCompleto})` : ""}`;
    case "partes.editar":
      return "Editó una parte";
    case "partes.desactivar":
      return "Eliminó una parte";
    case "honorarios.crear":
      return "Creó un honorario";
    case "honorarios.editar":
      return "Editó un honorario";
    case "honorarios.cambiar_estado":
      return `Cambió el estado del honorario de "${detalle.estadoAnterior ?? "?"}" a "${detalle.estadoNuevo ?? "?"}"`;
    case "honorarios.editar_cuotas":
      return "Regeneró el plan de cuotas de un honorario";
    case "honorarios.cobrar":
      return "Registró un cobro de honorario";
    case "rendiciones.crear":
      return "Creó una rendición";
    case "rendiciones.anular":
      return "Anuló una rendición";
    case "movimientos_economicos.crear":
      return `Registró un movimiento económico${detalle.tipo ? ` (${detalle.tipo})` : ""}`;
    default:
      return accion.replace(/[._]/g, " ");
  }
}

function parsearDetalle(detalle: string | null): Record<string, unknown> {
  if (!detalle) return {};
  try {
    return JSON.parse(detalle) as Record<string, unknown>;
  } catch {
    return {};
  }
}

export function PestanaHistorial({ expedienteId }: { expedienteId: string }) {
  const [items, setItems] = useState<AuditoriaEventoItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setItems(null);
    setError(null);
    api
      .get<{ items: AuditoriaEventoItem[] }>(`/expedientes/${expedienteId}/auditoria`)
      .then((d) => setItems(d.items))
      .catch((err) =>
        setError(err instanceof ErrorApi ? err.message : "No se pudo cargar el historial."),
      );
  }, [expedienteId]);

  if (error) {
    return <EstadoVacio titulo="No se pudo cargar el historial" descripcion={error} />;
  }
  if (items === null) return <Cargando />;
  if (items.length === 0) {
    return (
      <EstadoVacio
        titulo="Sin eventos registrados"
        descripcion="Todavía no hay actividad auditada sobre este expediente."
      />
    );
  }

  return (
    <Tarjeta>
      <h3 className="mb-3 text-sm font-semibold text-texto">Historial de actividad</h3>
      <ul className="space-y-2">
        {items.map((item) => {
          const detalle = parsearDetalle(item.detalle);
          return (
            <li key={item.id} className="border-b border-borde pb-2 text-sm last:border-0">
              <span className="font-medium text-texto">
                {ETIQUETAS_ENTIDAD[item.entidad_tipo] ?? item.entidad_tipo}
              </span>{" "}
              <span className="text-texto-secundario">— {descripcionEvento(item.accion, detalle)}</span>
              <div className="text-xs text-texto-secundario">
                {item.actor_nombre ?? "Usuario"} · {item.creado_en}
              </div>
            </li>
          );
        })}
      </ul>
    </Tarjeta>
  );
}
